import { ArrowDown, ArrowUp, Bold, FileText, Link2, MoreHorizontal, Paperclip, Plus, Trash2, X } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { JOB_FILE_EXTENSIONS, REQUEST_BRIEF_KEYS, hasBriefContent, type BriefSection } from '@nest-recruit/shared';
import { EditedMarksRow } from '../../../requisitions/components/EditedMark';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { useToasts } from '../../../../stores/toasts';
import { jobFileUrl, useSaveJobStep } from '../../api/jobs-api';
import { useStepAutosave } from '../../hooks/use-step-autosave';
import type { StepProps } from '../step-props';
import { BulletText } from './BulletText';
import { missingAction, sectionMissing } from './SectionMissing';
import { useAttachJobFile, useRemoveJobFile } from './job-files';
import styles from './builder.module.css';

/**
 * JOB-13's brief (F5.19, F5.20; JB-5…JB-7): sections of bullet points with bold and links,
 * nothing more, and files on any section. Built from our own pieces (DECISIONS-LOG "Start R3"
 * Q1): a bullet reads as formatted text and turns into a line of text to edit when clicked.
 * Enter starts the next bullet, Backspace on an empty one removes it. Saves as you type.
 */

function withoutEmptyPoints(brief: readonly BriefSection[]): readonly BriefSection[] {
  return brief.map((section) => ({ ...section, bullets: section.bullets.filter((bullet) => bullet.trim() !== '') }));
}

const ACCEPT = JOB_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');

export function BriefEditor({ job, isReadOnly, onConflict, footer }: StepProps): ReactNode {
  const [brief, setBrief] = useState<readonly BriefSection[]>(() => job.brief);
  const saveStep = useSaveJobStep();
  const showUndo = useToasts((toasts) => toasts.showUndo);
  const { state, schedule, flush } = useStepAutosave<readonly BriefSection[]>({
    job,
    onConflict,
    save: (next, version) => saveStep.mutateAsync({ id: job.id, step: 'brief', body: { version, brief: [...next] } }),
  });
  const { attach, isAttaching } = useAttachJobFile(job);
  const files = useRemoveJobFile(job);
  // Which bullet is open for editing, and where the caret goes when it opens.
  const [editing, setEditing] = useState<{ readonly section: string; readonly index: number } | null>(null);
  // One point under any heading completes the brief, so the note sits once, on the first card
  // (tester D31). It clears the moment a point is typed, before the save comes back.
  const missing = hasBriefContent(brief) ? [] : sectionMissing(job, 'brief');

  function change(next: readonly BriefSection[]): void {
    setBrief(next);
    // An empty point is only ever on screen while it is being typed; it never reaches the server.
    schedule(withoutEmptyPoints(next));
  }

  function changeSection(key: string, patch: (section: BriefSection) => BriefSection): void {
    change(brief.map((section) => (section.key === key ? patch(section) : section)));
  }

  function moveSection(index: number, by: number): void {
    const next = [...brief];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(index + by, 0, moved);
    change(next);
  }

  function removeSection(index: number): void {
    const removed = brief[index];
    if (!removed) return;
    const before = brief;
    change(brief.filter((_, i) => i !== index));
    showUndo(`${removed.title} removed`, () => change(before));
  }

  function addSection(): void {
    const key = `custom_${crypto.randomUUID().slice(0, 8)}`;
    change([...brief, { key, title: 'New section', bullets: [''] }]);
    setEditing({ section: key, index: 0 });
  }

  return (
    <div className={styles.section}>
      <EditedMarksRow marks={job.requestEditMarks} parts={REQUEST_BRIEF_KEYS} />
      {brief.map((section, sectionIndex) => {
        const attached = job.files.filter((file) => file.documentType === 'job_file' && file.sectionKey === section.key && !files.hidden.has(file.id));
        return (
          <Card key={section.key}>
            <div className={styles.briefHead}>
              <div className={styles.briefTitle}>
                <Input
                  aria-label="Section title"
                  value={section.title}
                  disabled={isReadOnly}
                  maxLength={80}
                  onChange={(event) => changeSection(section.key, (current) => ({ ...current, title: event.target.value }))}
                />
              </div>
              {sectionIndex === 0 && !isReadOnly && missingAction(missing)}
              {!isReadOnly && (
                <SectionMenu
                  title={section.title}
                  canMoveUp={sectionIndex > 0}
                  canMoveDown={sectionIndex < brief.length - 1}
                  canRemove={attached.length === 0}
                  onMove={(by) => moveSection(sectionIndex, by)}
                  onRemove={() => removeSection(sectionIndex)}
                />
              )}
            </div>
            <ul className={styles.bullets}>
              {section.bullets.map((bullet, index) => (
                <li key={index} className={styles.bullet}>
                  {editing?.section === section.key && editing.index === index && !isReadOnly ? (
                    <BulletInput
                      value={bullet}
                      label={`${section.title}, bullet ${index + 1}`}
                      onChange={(text) => changeSection(section.key, (current) => ({ ...current, bullets: current.bullets.map((b, i) => (i === index ? text : b)) }))}
                      onEnter={() => {
                        if (!bullet.trim()) return;
                        changeSection(section.key, (current) => ({ ...current, bullets: [...current.bullets.slice(0, index + 1), '', ...current.bullets.slice(index + 1)] }));
                        setEditing({ section: section.key, index: index + 1 });
                      }}
                      onRemoveEmpty={() => {
                        changeSection(section.key, (current) => ({ ...current, bullets: current.bullets.filter((_, i) => i !== index) }));
                        setEditing(index > 0 ? { section: section.key, index: index - 1 } : null);
                      }}
                      onMove={(by) => {
                        const target = index + by;
                        if (target >= 0 && target < section.bullets.length) setEditing({ section: section.key, index: target });
                      }}
                      onLeave={() => {
                        if (!bullet.trim()) {
                          changeSection(section.key, (current) => ({ ...current, bullets: current.bullets.filter((_, i) => i !== index) }));
                        }
                        setEditing(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.bulletView}
                      disabled={isReadOnly}
                      aria-label={`Edit: ${bullet || 'empty bullet'}`}
                      onClick={() => setEditing({ section: section.key, index })}
                    >
                      {bullet.trim() ? <BulletText text={bullet} /> : <span className={styles.placeholderText}>Write a point</span>}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {!isReadOnly && (
              <div className={styles.briefActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Plus}
                  onClick={() => {
                    changeSection(section.key, (current) => ({ ...current, bullets: [...current.bullets, ''] }));
                    setEditing({ section: section.key, index: section.bullets.length });
                  }}
                >
                  Add a point
                </Button>
                <FileButton isPending={isAttaching} label={`Attach a file to ${section.title}`} onFile={(file) => void attach(file, { sectionKey: section.key })} />
              </div>
            )}
            {attached.length > 0 && (
              <ul className={styles.fileList} aria-label={`Files on ${section.title}`}>
                {attached.map((file) => (
                  <li key={file.id} className={styles.fileChip}>
                    <FileText size={14} aria-hidden />
                    <a href={jobFileUrl(job.id, file.id)} target="_blank" rel="noopener noreferrer">
                      {file.fileName}
                    </a>
                    {!isReadOnly && (
                      <button type="button" className={styles.fileRemove} aria-label={`Remove ${file.fileName}`} onClick={() => files.remove(file.id, file.fileName)}>
                        <X size={14} aria-hidden />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      })}
      {!isReadOnly && (
        <div>
          <Button variant="outline" icon={Plus} onClick={addSection} disabled={brief.length >= 12}>
            Add section
          </Button>
        </div>
      )}
      {footer({ state, flush })}
    </div>
  );
}

/** One bullet being edited, with Bold and Link for the selected words (JB-5). */
function BulletInput({
  value,
  label,
  onChange,
  onEnter,
  onRemoveEmpty,
  onMove,
  onLeave,
}: {
  readonly value: string;
  readonly label: string;
  readonly onChange: (text: string) => void;
  readonly onEnter: () => void;
  readonly onRemoveEmpty: () => void;
  readonly onMove: (by: number) => void;
  readonly onLeave: () => void;
}): ReactNode {
  const inputRef = useRef<HTMLInputElement | null>(null);

  /** Wraps the selected words; with nothing selected, opens the marks with the caret between them. */
  function wrap(before: string, after: string, sample: string): void {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    const chosen = value.slice(start, end) || sample;
    onChange(`${value.slice(0, start)}${before}${chosen}${after}${value.slice(end)}`);
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(start + before.length, start + before.length + chosen.length);
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      onEnter();
    } else if (event.key === 'Backspace' && value === '') {
      event.preventDefault();
      onRemoveEmpty();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      onMove(event.key === 'ArrowUp' ? -1 : 1);
    } else if (event.key === 'Escape') {
      onLeave();
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
      event.preventDefault();
      wrap('**', '**', '');
    }
  }

  return (
    <div className={styles.bulletEdit}>
      <Input
        ref={inputRef}
        autoFocus
        aria-label={label}
        value={value}
        maxLength={500}
        placeholder="Write a point"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={(event) => {
          // Clicking Bold or Link keeps the bullet open; anything else closes it.
          if (event.relatedTarget instanceof HTMLElement && event.relatedTarget.dataset.bulletTool !== undefined) return;
          onLeave();
        }}
      />
      <span className={styles.bulletTools}>
        <button type="button" data-bullet-tool="" className={styles.toolButton} aria-label="Bold (Ctrl+B)" onMouseDown={(event) => event.preventDefault()} onClick={() => wrap('**', '**', '')}>
          <Bold size={15} aria-hidden />
        </button>
        <button type="button" data-bullet-tool="" className={styles.toolButton} aria-label="Link" onMouseDown={(event) => event.preventDefault()} onClick={() => wrap('[', '](https://)', 'link text')}>
          <Link2 size={15} aria-hidden />
        </button>
      </span>
    </div>
  );
}

function SectionMenu({
  title,
  canMoveUp,
  canMoveDown,
  canRemove,
  onMove,
  onRemove,
}: {
  readonly title: string;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly canRemove: boolean;
  readonly onMove: (by: number) => void;
  readonly onRemove: () => void;
}): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const act = (run: () => void) => (): void => {
    close();
    run();
  };
  return (
    <>
      <button ref={triggerRef} type="button" className={styles.iconButton} aria-label={`More for ${title}`} onClick={toggle}>
        <MoreHorizontal size={16} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label={`Actions for ${title}`} align="right">
        <MenuItem icon={<ArrowUp size={15} />} disabled={!canMoveUp} onClick={act(() => onMove(-1))}>
          Move up
        </MenuItem>
        <MenuItem icon={<ArrowDown size={15} />} disabled={!canMoveDown} onClick={act(() => onMove(1))}>
          Move down
        </MenuItem>
        <MenuItem icon={<Trash2 size={15} />} isDanger disabled={!canRemove} {...(canRemove ? {} : { meta: 'Remove its files first' })} onClick={act(onRemove)}>
          Remove section
        </MenuItem>
      </Popover>
    </>
  );
}

/** Opens the file chooser; our button, the browser's chooser behind it. */
export function FileButton({
  label,
  isPending,
  onFile,
  accept = ACCEPT,
  children = 'Attach a file',
}: {
  readonly label: string;
  readonly isPending: boolean;
  readonly onFile: (file: File) => void;
  readonly accept?: string;
  readonly children?: ReactNode;
}): ReactNode {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className={styles.hiddenFile}
        accept={accept}
        aria-label={label}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onFile(file);
        }}
      />
      <Button variant="ghost" size="sm" icon={Paperclip} isPending={isPending} onClick={() => inputRef.current?.click()}>
        {children}
      </Button>
    </>
  );
}
